import React, { useRef } from "react";

import { Box, Stack, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useDateFormatSelect } from "store";
import type { ReportMeta } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { EditableField } from "./EditableField";

interface TitlePageContentProps {
  meta: ReportMeta;
  // Per-page heading (falls back to meta at the call site). Edited independently of the global meta.
  title: string;
  subtitle: string;
  pageHeight: number;
  // Preview/PDF: render everything static (no editing affordances) so canvas and preview match 1:1.
  readOnly?: boolean;
  onTitleChange?: (value: string) => void;
  onSubtitleChange?: (value: string) => void;
  onUpdate?: (updates: Partial<ReportMeta>) => void;
  onLogoFileChange?: (file: File | null) => void;
}

const noop = () => {};

export const TitlePageContent: React.FC<TitlePageContentProps> = ({
  meta,
  title,
  subtitle,
  pageHeight,
  readOnly = false,
  onTitleChange,
  onSubtitleChange,
  onUpdate,
  onLogoFileChange
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const dateFormat = useDateFormatSelect();

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onUpdate?.({ logo: reader.result as string });
    reader.readAsDataURL(file);
    onLogoFileChange?.(file);
    event.target.value = "";
  };

  const asOfText = meta.asOfDate ? dayjs(meta.asOfDate).format(dateFormat) : "";

  return (
    <Box
      sx={{
        height: pageHeight,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#071f35",
        position: "relative",
        p: "0 48px"
      }}
    >
      {!readOnly && (
        <>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
          <Typography
            sx={{
              position: "absolute",
              top: 12,
              right: 16,
              fontSize: 9,
              fontWeight: 600,
              color: "rgba(255,255,255,0.35)",
              letterSpacing: "0.04em"
            }}
          >
            Click fields to edit
          </Typography>
        </>
      )}

      {(meta.logo || !readOnly) && (
        <Box
          sx={{
            position: "relative",
            width: 88,
            height: 88,
            mb: 4,
            "&:hover .logo-remove": { opacity: 1 }
          }}
        >
          <Box
            onClick={
              readOnly
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    fileRef.current?.click();
                  }
            }
            title={readOnly ? undefined : meta.logo ? "Change logo" : "Upload logo"}
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "16px",
              border: meta.logo || readOnly ? "none" : "2px dashed rgba(255,255,255,0.3)",
              bgcolor: meta.logo ? "rgba(255,255,255,0.12)" : readOnly ? "transparent" : "rgba(255,255,255,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: readOnly ? "default" : "pointer",
              overflow: "hidden"
            }}
          >
            {meta.logo ? (
              <Box
                component="img"
                src={meta.logo}
                alt="Logo"
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : readOnly ? null : (
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>LOGO</Typography>
            )}
          </Box>
          {!readOnly && meta.logo && (
          <Box
            className="logo-remove"
            component="button"
            onClick={(e) => {
              e.stopPropagation();
              onUpdate?.({ logo: "" });
              onLogoFileChange?.(null);
            }}
            sx={{
              position: "absolute",
              top: -6,
              right: -6,
              width: 20,
              height: 20,
              borderRadius: "50%",
              border: "none",
              bgcolor: "#DC2626",
              color: "#FFFFFF",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
              transition: "opacity 150ms ease-out",
              zIndex: 1
            }}
          >
            {"\u2715"}
          </Box>
          )}
        </Box>
      )}

      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: "rgba(255,255,255,0.45)",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          mb: "14px"
        }}
      >
        Portfolio Report
      </Typography>

      <EditableField
        value={title}
        placeholder="Report title"
        onSave={onTitleChange ?? noop}
        readOnly={readOnly}
        style={{ fontSize: 36, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.2, textAlign: "center", maxWidth: 700 }}
      />
      {(!readOnly || subtitle) && (
        <EditableField
          value={subtitle}
          placeholder="Add subtitle"
          onSave={onSubtitleChange ?? noop}
          readOnly={readOnly}
          style={{ fontSize: 16, color: "rgba(255,255,255,0.68)", marginTop: 12, textAlign: "center" }}
        />
      )}

      <Stack
        direction="column"
        sx={{ mt: 6, display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.45)", mb: "4px" }}>
            As of Date
          </Typography>
          {readOnly ? (
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#FFFFFF" }}>{asOfText}</Typography>
          ) : (
            <DesktopDatePicker
              format={dateFormat}
              value={meta.asOfDate ? dayjs(meta.asOfDate) : null}
              onChange={(value) => onUpdate?.({ asOfDate: value ? toIsoMidday(value) : "" })}
              slotProps={{
                textField: {
                  variant: "standard",
                  placeholder: "Select date",
                  InputProps: { disableUnderline: true },
                  onClick: (e) => e.stopPropagation(),
                  sx: {
                    "& .MuiInputBase-input": {
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#FFFFFF",
                      textAlign: "center",
                      p: 0,
                      cursor: "pointer"
                    },
                    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.55)", fontSize: 16 }
                  }
                },
                popper: { sx: { zIndex: 1500 } }
              }}
            />
          )}
        </Box>
        {(!readOnly || meta.preparedBy) && (
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.45)", mb: "4px" }}>
              Prepared by
            </Typography>
            {readOnly ? (
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#FFFFFF" }}>{meta.preparedBy}</Typography>
            ) : (
              <EditableField
                value={meta.preparedBy}
                placeholder="Your name"
                onSave={(v) => onUpdate?.({ preparedBy: v })}
                style={{ fontSize: 16, fontWeight: 600, color: "#FFFFFF", textAlign: "center" }}
              />
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
};
